import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const SCRIPT_URLS = {
  score: "https://static.trustoo.nl/widget/widget_v2.js",
  review: "https://static.trustoo.nl/widget/widget_review.js",
};

let configPromise = null;
const scriptPromises = {};

export function fetchTrustooConfig() {
  if (!configPromise) {
    configPromise = api.get("/trustoo/config").then((r) => r.data).catch((e) => {
      configPromise = null;
      throw e;
    });
  }
  return configPromise;
}

export function loadTrustooScript(kind) {
  const src = SCRIPT_URLS[kind];
  if (!src) return Promise.reject(new Error("Unknown Trustoo widget kind"));
  if (typeof window === "undefined") return Promise.reject();
  if (scriptPromises[kind]) return scriptPromises[kind];

  scriptPromises[kind] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "1") return resolve();
      existing.addEventListener("load", () => { existing.dataset.loaded = "1"; resolve(); });
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => { s.dataset.loaded = "1"; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromises[kind];
}

/**
 * Try every known Trustoo re-hydration hook so widgets mounted after the
 * initial script load (e.g. SPA route changes / language switches) still render.
 */
export function reinitTrustoo() {
  if (typeof window === "undefined") return;
  const candidates = [
    () => window.TrustooWidgets?.init?.(),
    () => window.Trustoo?.init?.(),
    () => window.trustoo?.init?.(),
    () => window.TrustooWidgets?.reinit?.(),
    () => window.TrustooScoreWidget?.init?.(),
  ];
  for (const call of candidates) {
    try { call(); } catch (_) { /* ignore */ }
  }
}

/**
 * Editorial Trustoo score badge with a framed wrapper.
 * Re-initialises the widget on every mount so it survives SPA navigations
 * (e.g. switching between /nl, /en, /hu).
 */
export function TrustooScoreBadge({ variant = "light", className = "" }) {
  const { t } = useTranslation();
  const [token, setToken] = useState(null);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0); // used to force widget div remount
  const mountRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Defer to idle so it doesn't compete with the hero paint.
    const boot = () => {
      if (cancelled) return;
      fetchTrustooConfig()
        .then((c) => { if (!cancelled) setToken(c.widget_token); })
        .catch(() => { if (!cancelled) setError(true); });
    };
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(boot, { timeout: 3000 });
      return () => { cancelled = true; try { cancelIdleCallback(id); } catch (_) {} };
    }
    const id = setTimeout(boot, 1800);
    return () => { cancelled = true; clearTimeout(id); };
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    loadTrustooScript("score")
      .then(() => {
        if (cancelled) return;
        // First scan on load
        reinitTrustoo();
        // Follow-up scans in case script mounts widgets asynchronously.
        const retries = [80, 220, 500, 1200];
        retries.forEach((ms) => setTimeout(() => { if (!cancelled) reinitTrustoo(); }, ms));
      })
      .catch(() => setError(true));
    return () => { cancelled = true; };
  }, [token, tick]);

  if (error || !token) return null;

  const dark = variant === "dark";
  return (
    <div className={`trustoo-badge-wrap max-w-full w-fit ${className}`} data-testid="trustoo-score-badge-wrap">
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex items-stretch max-w-full w-fit"
      data-testid="trustoo-score-badge"
    >
      {/* Left rail — editorial verified plate */}
      <div
        className={`flex items-center gap-2 px-2.5 sm:px-3 py-2 border-y border-l shrink-0 ${
          dark
            ? "border-white/15 bg-white/[0.06] text-white"
            : "border-slate-300 bg-white text-[#0A1128]"
        }`}
      >
        <span className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-[#FF5A1F] shrink-0">
          <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={2.4} />
        </span>
        <div className="flex flex-col leading-tight">
          <span className={`text-[8px] sm:text-[9px] uppercase tracking-[0.15em] sm:tracking-[0.25em] font-bold whitespace-nowrap ${dark ? "text-white/60" : "text-slate-500"}`}>
            {t("reviews.source_label", { defaultValue: "Trustoo verified" })}
          </span>
          <span className="font-display text-[11px] sm:text-[13px] uppercase tracking-tight whitespace-nowrap">
            Rating · Live
          </span>
        </div>
      </div>

      {/* Right — the actual Trustoo widget, key forces re-mount on tick change */}
      <div
        ref={mountRef}
        className={`trustoo-score-wrapper flex items-center px-2 sm:px-3 py-2 border min-w-0 ${
          dark ? "border-white/15 bg-white/[0.03]" : "border-slate-300 bg-slate-50"
        } ${dark ? "trustoo-on-dark" : ""}`}
      >
        <div
          className="trustoo-widget"
          data-id={token}
          data-theme={dark ? "dark" : "light"}
          key={`${token}-${tick}`}
        />
      </div>

      {/* Bottom accent line */}
      <span className="absolute left-0 -bottom-1 h-[2px] w-16 bg-[#FF5A1F]" />
    </motion.div>
    </div>
  );
}
